// A one-card-at-a-time deck that wraps in both directions.
//
// This replaces a native overflow-x scroller. That scroller could show one
// card at a time by snapping, but it could not wrap: reaching the last squad
// left the only way back a swipe through every card in between, and native
// scroll has no notion of "after the end comes the beginning". Clone-based
// infinite scrollers exist, but they work by silently jumping the scroll
// position mid-momentum, which iOS fights.
//
// So the deck is index-driven instead. Only three slots are ever rendered,
// holding the previous, current and next item by modular arithmetic, and the
// track sits translated one slot to the left so the current card is the one
// on screen. Moving means animating the track by one slot and then renumbering
// the window with the transition switched off, which lands back in the same
// visual state with a new current index. Because the neighbours are computed
// modulo the length, the card after the last one is the first one, with no
// special case anywhere.
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

// How far a drag has to travel before it counts as a swipe rather than a
// tap that wandered. A fraction of the width rather than a fixed pixel count,
// so the gesture feels the same on a phone and on a desktop window.
const SWIPE_FRACTION = 0.2;
const SWIPE_MIN_PX = 40;

/**
 * The item `steps` places after `index`, wrapping in both directions.
 * Exported for its own test: off-by-one here is invisible until the deck is
 * at one end, which is exactly where wrapping matters.
 */
export function wrapIndex(index, steps, length) {
  if (length <= 0) return 0;
  return ((index + steps) % length + length) % length;
}

// The previous/next buttons live in the section header, above and outside
// this component, so the imperative handle is what lets them drive it without
// the parent owning an index it would then have to keep in step with a drag
// already in flight.
const SwipeDeck = forwardRef(function SwipeDeck(
  { length, renderAt, label = "item", onIndexChange, initialIndex = 0 },
  ref
) {
  // Lazy initializer, not a post-mount jump: a caller with an initial target
  // (a deep link to one manager's card) hands it in before first paint, so
  // the deck opens there directly. Setting it via an imperative jumpTo() call
  // from the caller's own mount effect used to lose the update, because that
  // effect runs in the same commit this component first mounts in and races
  // React's own passive-effect ordering for a freshly mounted child.
  const [index, setIndex] = useState(() => wrapIndex(initialIndex, 0, length));
  // Pixels the track is dragged from rest. Positive means the finger moved
  // right, which reveals the previous card.
  const [drag, setDrag] = useState(0);
  // The direction being animated out, or 0 when at rest. While non-zero the
  // track carries a transition and the pointer is ignored.
  const [settling, setSettling] = useState(0);

  const trackRef = useRef(null);
  const gesture = useRef(null);
  // Set when a gesture actually moved, and read by the capture-phase click
  // handler below. Cards hold their own tap targets, and a pointerup that
  // ends a swipe still fires a click on whatever is under the finger.
  const swallowClick = useRef(false);

  // Clamp back into range if the league shrinks under us (a manager leaving,
  // or a week with fewer lineups than the last one viewed).
  useEffect(() => {
    if (index >= length && length > 0) setIndex(0);
  }, [index, length]);

  useEffect(() => { onIndexChange?.(index); }, [index, onIndexChange]);

  // One slot at a time, and only one slot: the rendered window holds the
  // immediate neighbours and nothing beyond them, so there is no card to
  // animate past. Anything further is a jump.
  const go = useCallback((dir) => {
    if (length <= 1 || settling || !dir) return;
    setSettling(dir > 0 ? 1 : -1);
  }, [length, settling]);

  const jumpTo = useCallback((target) => {
    if (settling) return;
    setDrag(0);
    setIndex(wrapIndex(target, 0, length));
  }, [length, settling]);

  useImperativeHandle(ref, () => ({ go, jumpTo }), [go, jumpTo]);

  // Committed once the slide animation finishes, so the renumbering is
  // invisible: the new current card is already the one under the viewport.
  const commit = () => {
    if (!settling) return;
    setIndex((i) => wrapIndex(i, settling, length));
    setSettling(0);
    setDrag(0);
  };

  const onPointerDown = (e) => {
    if (length <= 1 || settling) return;
    // Ignore secondary buttons and anything starting on a control: a card
    // holds its own buttons, and a press on one should not become a drag.
    if (e.button != null && e.button !== 0) return;
    gesture.current = { x: e.clientX, y: e.clientY, locked: null, id: e.pointerId };
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;

    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;

    // Decide once whether this gesture is a horizontal swipe or a vertical
    // scroll, and never take it back. Without the lock, a mostly-vertical
    // scroll drags the deck sideways a few pixels on every flick.
    if (g.locked === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      g.locked = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (g.locked === "x") trackRef.current?.setPointerCapture?.(e.pointerId);
    }
    if (g.locked !== "x") return;

    setDrag(dx);
  };

  const onPointerUp = (e) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.locked !== "x") { setDrag(0); return; }

    if (Math.abs(drag) > 4) swallowClick.current = true;

    const width = trackRef.current?.offsetWidth || 1;
    const threshold = Math.max(SWIPE_MIN_PX, width * SWIPE_FRACTION);

    if (drag <= -threshold) go(1);
    else if (drag >= threshold) go(-1);
    else setDrag(0); // Not far enough: fall back to where it started.
  };

  if (length <= 0) return null;

  // The three slots. With one item they are all the same card, which is
  // correct: a single-squad league has nowhere to swipe to.
  const slots = [wrapIndex(index, -1, length), index, wrapIndex(index, 1, length)];

  // At rest the track sits one slot left, so the middle slot is on screen.
  // Settling adds a further slot in the direction of travel.
  const offsetPct = -100 - (settling * 100);

  return (
    <div className="relative overflow-hidden" aria-roledescription="carousel">
      <div
        ref={trackRef}
        className="flex"
        style={{
          // pan-y leaves vertical scrolling to the browser while this handles
          // horizontal, which is what keeps the page scrollable through the
          // deck rather than trapping the finger in it.
          touchAction: "pan-y",
          transform: `translateX(calc(${offsetPct}% + ${settling ? 0 : drag}px))`,
          transition: settling ? "transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)" : "none",
        }}
        onTransitionEnd={commit}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={(e) => {
          if (!swallowClick.current) return;
          swallowClick.current = false;
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {slots.map((slotIndex, position) => (
          <div
            key={`${slotIndex}-${position}`}
            className="flex-shrink-0 w-full"
            // Only the middle slot is on screen. The neighbours are real DOM,
            // so they are announced too without this.
            aria-hidden={position !== 1}
          >
            {renderAt(slotIndex)}
          </div>
        ))}
      </div>

      <span className="sr-only" aria-live="polite">
        {`${label} ${index + 1} of ${length}`}
      </span>
    </div>
  );
});

export default SwipeDeck;
